import * as React from "react";
import useSWR from "swr";
import Swal from "sweetalert2";
import { Select, Space, Spin, Typography, Divider, Pagination } from "antd";

import { snackbarStore } from "src/store/snackbarStore";
import { useRewardStore } from "src/store/reward/reward.store";
import RewardTable from "src/components/reward/RewardTable";
import {
  getRewardListApi,
  deleteRewardApi,
  type RewardListItem,
} from "src/api/reward/rewardApi";
import CustomButton from "src/components/common/CustomButton";
import RewardUpsertModal from "src/components/reward/RewardUpsertModal";

const SIZE_OPTIONS = [20, 50, 100];

export default function RewardListTab() {
  const { page, size, sort, setQuery } = useRewardStore();

  const swrKey = React.useMemo(
    () => ["reward/list", page, size, sort] as const,
    [page, size, sort]
  );

  const { data, isLoading, mutate } = useSWR(
    swrKey,
    async () => {
      const apiPage = Math.max(0, page - 1);
      return await getRewardListApi({
        page: apiPage,
        size,
        sort: sort || undefined,
      });
    },
    { revalidateOnFocus: false, keepPreviousData: true }
  );

  const list = data?.data?.content ?? [];
  const totalElements = data?.data?.page?.totalElements ?? 0;

  const [saving, setSaving] = React.useState(false);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [editingItem, setEditingItem] = React.useState<RewardListItem | null>(null);

  const openCreateModal = () => {
    setEditingItem(null);
    setModalOpen(true);
  };

  const openEditModal = (item: RewardListItem) => {
    setEditingItem(item);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingItem(null);
  };

  const onDelete = async (item: RewardListItem) => {
    const result = await Swal.fire({
      title: "삭제 확인",
      text: `"${item.rewardName}" 삭제하시겠습니까?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "삭제",
      cancelButtonText: "취소",
      didOpen: () => {
        const container = Swal.getContainer();
        if (container) {
          container.style.zIndex = "2000";
        }
      },
    });
    if (!result.isConfirmed) return;

    try {
      setSaving(true);
      const res = await deleteRewardApi(item.rewardId);
      if (res.statusCode !== 1) {
        snackbarStore.getState().addSnackbar({
          message: res.message || "삭제 실패",
          severity: "error",
        });
        return;
      }

      snackbarStore.getState().addSnackbar({ message: "삭제 완료", severity: "success" });
      await mutate();
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitSuccess = async () => {
    await mutate();
    closeModal();
  };

  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
      >
        <Typography.Text type="secondary">총 {totalElements.toLocaleString()}건</Typography.Text>

        <CustomButton variant="contained" onClick={openCreateModal} disabled={saving}>
          등록
        </CustomButton>
      </div>

      <Space wrap size={12} align="center" style={{ marginBottom: 16 }}>
        <CustomButton variant="contained" onClick={() => mutate()} disabled={isLoading}>
          검색
        </CustomButton>

        <div style={{ flex: 1 }} />

        <Select
          value={size}
          onChange={(value) => setQuery({ size: Number(value) || 20, page: 1 })}
          style={{ width: 140 }}
          options={SIZE_OPTIONS.map((s) => ({ value: s, label: `${s}개` }))}
        />
      </Space>

      <Divider style={{ margin: "8px 0 16px" }} />

      {isLoading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "48px 0" }}>
          <Spin />
        </div>
      ) : (
        <>
          <RewardTable
            list={list}
            loading={false}
            disabled={saving}
            onEdit={openEditModal}
            onDelete={onDelete}
          />

          <div style={{ display: "flex", justifyContent: "center", paddingTop: 16 }}>
            <Pagination
              current={page}
              total={Math.max(1, totalElements)}
              pageSize={size}
              onChange={(nextPage) => setQuery({ page: nextPage })}
              showSizeChanger={false}
              disabled={isLoading || saving}
            />
          </div>
        </>
      )}

      <RewardUpsertModal
        open={modalOpen}
        editingItem={editingItem}
        onClose={closeModal}
        onSuccess={handleSubmitSuccess}
      />
    </>
  );
}
